import { useCallback, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Toast from "@/components/feature/Toast";
import { faqs } from "@/mocks/dramas";
import { usePageMeta } from "@/hooks/usePageMeta";

const team = [
  {
    name: "沈书",
    role: "主编 · 剧集内容",
    desc: "十年影视媒体编辑，负责剧集资料的整理与校对，坚持每一条剧情概述都要能被剧集原片验证。",
  },
  {
    name: "林见川",
    role: "专栏作者 · 剧情解析",
    desc: "专注叙事结构与人物动机分析，喜欢把一部剧拆成时间线、动机表和镜头语言三个部分来读。",
  },
  {
    name: "周回",
    role: "社区与反馈",
    desc: "负责收集剧迷的推荐与纠错反馈，也是把「下周写哪部剧」这个问题问得最多的人。",
  },
];

const contactList = [
  { icon: "ri-mail-line", label: "编辑邮箱", value: "editor@dramajournal.example" },
  { icon: "ri-weibo-line", label: "官方微博", value: "@波多野结衣家庭教师" },
  { icon: "ri-time-line", label: "回复时间", value: "工作日 10:00 - 19:00" },
];

type FormResponse = {
  code?: string;
  meta?: { message?: string; detail?: string };
  message?: string;
};

const FORM_ENDPOINT = "https://readdy.ai/api/form/dbb6nia9ke8mc8ad7f40";

export default function About() {
  usePageMeta({
    title: "关于我们 - 内容说明与反馈 - 波多野结衣家庭教师",
    description:
      "了解波多野结衣家庭教师的内容定位、编辑团队与内容标准，查看常见问题说明，并通过反馈表单向我们提交剧集推荐或纠错建议。",
    keywords: "关于我们,影视内容平台,剧情介绍,编辑团队,意见反馈,波多野结衣家庭教师",
  });

  const [toast, setToast] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const closeToast = useCallback(() => setToast(""), []);

  const copyValue = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setToast(`${label}已复制：${value}`);
    } catch {
      setToast("当前浏览器不支持自动复制，请手动选取文字复制");
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam = `${serverMessage} ${responseText}`.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请检查填写内容后重试。"
            : serverMessage || "提交失败，请稍后再试一次。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，反馈未能提交，请稍后再试。");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-[72px]">
        <section className="border-b border-background-200/70 bg-background-100/40">
          <div className="mx-auto grid w-full max-w-[1240px] items-center gap-8 px-4 py-10 md:px-6 md:py-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
            <div>
              <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.28em] text-primary-500">
                <span className="h-px w-6 bg-primary-500"></span>
                关于本站
              </p>
              <h1 className="mt-4 font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-[42px]">
                把每一部剧的剧情
                <br className="hidden sm:block" />
                讲清楚，也讲透
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                波多野结衣家庭教师是一个专注热门电视剧的内容演示站。我们不做资源聚合，也不提供完整剧集播放，只把剧情梗概、人物关系与叙事解析整理好，让你在决定追一部剧之前，先读懂它值不值得。
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["剧情梗概", "角色分析", "叙事结构", "章节目录", "剧迷反馈"].map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-accent-500/12 px-3 py-1 font-label text-[11px] text-accent-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/category"
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
                >
                  浏览剧集库
                  <i className="ri-arrow-right-line text-sm"></i>
                </Link>
                <a
                  href="#feedback"
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 px-5 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-accent-500/60 hover:text-accent-600"
                >
                  <i className="ri-chat-3-line text-sm"></i>
                  提交反馈
                </a>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-lg border border-background-200/70">
              <div className="aspect-[4/3] w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20warm-lit%20home%20study%20filled%20with%20film%20books%20and%20stacked%20DVD%20cases%2C%20amber%20desk%20lamp%20glow%2C%20a%20notebook%20with%20handwritten%20notes%20open%20on%20the%20desk%2C%20deep%20shadow%20background%2C%20cozy%20editorial%20atmosphere%2C%20fine%20film%20grain%2C%20no%20text&width=900&height=675&seq=about-study-room&orientation=landscape"
                  alt="编辑部整理剧情笔记的工作台"
                  title="编辑团队 剧情资料整理"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background-50/85 via-transparent to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3">
                <p className="font-label text-[11px] text-accent-500">编辑部工作日常 · 剧情资料校对</p>
                <span className="rounded-full bg-background-50/75 px-2.5 py-1 font-label text-[10px] text-foreground-700">
                  Demo Content
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid gap-4 md:grid-cols-3">
            {[
              {
                icon: "ri-file-text-line",
                title: "内容以剧情为核心",
                desc: "每部剧都提供剧情梗概、人物动机与叙事解析，不写夸张标题，也不做剧透式引导。",
              },
              {
                icon: "ri-shield-check-line",
                title: "不提供完整播放",
                desc: "详情页的视频区域仅为播放器界面演示，不含真实片源与下载入口，避免误导用户。",
              },
              {
                icon: "ri-user-heart-line",
                title: "反馈会被读到",
                desc: "剧集推荐、剧情纠错与使用建议都可以通过反馈表单提交，编辑团队会定期整理。",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200/70 bg-background-100 p-5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500/12 text-primary-500">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {item.title}
                </h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-y border-background-200/60 bg-background-100/40">
          <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
            <div className="max-w-2xl">
              <p className="font-label text-[11px] uppercase tracking-[0.28em] text-primary-500">
                编辑团队
              </p>
              <h2 className="mt-2.5 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
                三个人的编辑部，一个共同的偏好
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                团队成员均为虚拟示例身份，用于展示内容型站点的团队介绍结构。
              </p>
            </div>
            <div className="mt-7 grid gap-4 md:grid-cols-3">
              {team.map((member) => (
                <div
                  key={member.name}
                  className="rounded-lg border border-background-200/70 bg-background-100 p-5"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-100 font-heading text-base text-secondary-900">
                      {member.name.slice(0, 1)}
                    </span>
                    <div>
                      <p className="font-heading text-sm font-semibold text-foreground-950">
                        {member.name}
                      </p>
                      <p className="font-label text-[11px] text-accent-600">{member.role}</p>
                    </div>
                  </div>
                  <p className="mt-3.5 text-xs leading-relaxed text-foreground-600">{member.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-2xl font-semibold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                  <i className="ri-question-answer-line text-base"></i>
                </span>
                常见问题
              </h2>
              <div className="mt-5 divide-y divide-background-200/70 rounded-lg border border-background-200/70 bg-background-100/70">
                {faqs.map((faq, index) => {
                  const open = openFaq === index;
                  return (
                    <div key={faq.question}>
                      <button
                        type="button"
                        onClick={() => setOpenFaq(open ? null : index)}
                        aria-expanded={open}
                        className="flex w-full cursor-pointer items-center justify-between gap-4 px-4 py-4 text-left md:px-5"
                      >
                        <span className="text-sm text-foreground-900">{faq.question}</span>
                        <i
                          className={`${
                            open ? "ri-subtract-line text-primary-500" : "ri-add-line text-foreground-500"
                          } shrink-0 text-base`}
                        ></i>
                      </button>
                      {open && (
                        <p className="animate-fade px-4 pb-4 text-xs leading-[1.9] text-foreground-600 md:px-5">
                          {faq.answer}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h2 className="flex items-center gap-2 font-heading text-2xl font-semibold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                  <i className="ri-contacts-line text-base"></i>
                </span>
                联系方式
              </h2>
              <div className="mt-5 rounded-lg border border-background-200/70 bg-background-100 p-4 md:p-5">
                <ul className="space-y-3">
                  {contactList.map((item) => (
                    <li
                      key={item.label}
                      className="flex items-center gap-3 rounded-md border border-background-200/60 bg-background-50/60 p-3"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-500/12 text-accent-600">
                        <i className={`${item.icon} text-base`}></i>
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-label text-[11px] text-foreground-500">{item.label}</p>
                        <p className="truncate text-sm text-foreground-900">{item.value}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => copyValue(item.value, item.label)}
                        aria-label={`复制${item.label}`}
                        className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300/60 text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-500"
                      >
                        <i className="ri-file-copy-line text-sm"></i>
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-[11px] leading-relaxed text-foreground-500">
                  以上联系方式为演示信息。合作、剧集推荐与内容纠错建议，优先使用下方反馈表单。
                </p>
              </div>

              <div className="mt-4 rounded-lg border border-primary-500/25 bg-primary-50 p-4">
                <p className="font-heading text-sm font-semibold text-foreground-950">内容声明</p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-700">
                  站内剧集资料、剧照与剧情概述均为演示内容，用于展示内容结构。本站不存储、不传播任何影片资源，也不提供下载服务。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="feedback" className="border-t border-background-200/60 bg-background-100/40">
          <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
            <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-12">
              <div>
                <p className="font-label text-[11px] uppercase tracking-[0.28em] text-primary-500">
                  意见反馈
                </p>
                <h2 className="mt-2.5 font-heading text-2xl font-semibold leading-snug text-foreground-950 md:text-3xl">
                  想看的剧没收录？
                  <br />
                  告诉我们
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  填写剧名、年份与推荐理由，或者指出某篇剧情介绍的错漏。表单提交后会直接进入编辑部的工作清单。
                </p>
                <ul className="mt-5 space-y-2 text-xs text-foreground-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line mt-0.5 text-accent-500"></i>
                    反馈平均 2 个工作日内查看
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line mt-0.5 text-accent-500"></i>
                    纠错内容会优先核实与更新
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line mt-0.5 text-accent-500"></i>
                    不会将你的邮箱用于任何推广
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border border-background-200/70 bg-background-100 p-5 md:p-6">
                {status === "success" ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-500/15 text-accent-600">
                      <i className="ri-check-double-line text-2xl"></i>
                    </span>
                    <p className="mt-4 font-heading text-lg text-foreground-950">谢谢你的反馈</p>
                    <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-600">
                      我们已经收到你的信息，编辑部会尽快查看。如果你留下了邮箱，我们会优先回信。
                    </p>
                    <button
                      type="button"
                      onClick={() => setStatus("idle")}
                      className="mt-5 cursor-pointer rounded-md border border-background-300/60 px-4 py-2 font-label text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500"
                    >
                      再写一条
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form="feedback-form"
                    className="flex flex-col gap-4"
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="min-w-0">
                        <label htmlFor="nickname" className="mb-1.5 block font-label text-xs text-foreground-600">
                          称呼
                        </label>
                        <input
                          id="nickname"
                          name="nickname"
                          type="text"
                          required
                          maxLength={40}
                          placeholder="怎么称呼你"
                          className="box-border h-10 w-full rounded-md border border-background-300/70 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                        />
                      </div>
                      <div className="min-w-0">
                        <label htmlFor="email" className="mb-1.5 block font-label text-xs text-foreground-600">
                          邮箱
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          placeholder="name@example.com"
                          className="box-border h-10 w-full rounded-md border border-background-300/70 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                        />
                      </div>
                    </div>

                    <div className="min-w-0">
                      <label htmlFor="feedback_type" className="mb-1.5 block font-label text-xs text-foreground-600">
                        反馈类型
                      </label>
                      <div className="relative">
                        <select
                          id="feedback_type"
                          name="feedback_type"
                          defaultValue="剧集推荐"
                          className="box-border h-10 w-full cursor-pointer appearance-none rounded-md border border-background-300/70 bg-background-50 px-3 pr-9 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                        >
                          <option value="剧集推荐">剧集推荐</option>
                          <option value="剧情纠错">剧情纠错</option>
                          <option value="功能建议">功能建议</option>
                          <option value="内容合作">内容合作</option>
                        </select>
                        <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
                      </div>
                    </div>

                    <div className="min-w-0">
                      <label htmlFor="message" className="mb-1.5 block font-label text-xs text-foreground-600">
                        反馈内容
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        placeholder="写下剧名、年份与想说的内容（最多 500 字）"
                        className="box-border w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      ></textarea>
                      <p className="mt-1 text-right font-label text-[10px] text-foreground-500">
                        最多 500 字
                      </p>
                    </div>

                    <div className="form-alt-field" aria-hidden="true">
                      <label htmlFor="website_alt">请勿填写此项</label>
                      <input
                        id="website_alt"
                        name="website_alt"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                      />
                    </div>

                    {status === "error" && formError && (
                      <p className="flex items-start gap-2 rounded-md border border-primary-500/30 bg-primary-500/10 px-3 py-2.5 text-xs leading-relaxed text-primary-900">
                        <i className="ri-error-warning-line mt-0.5 text-sm text-primary-500"></i>
                        {formError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className={`flex w-full cursor-pointer items-center justify-center gap-2 rounded-md py-3 font-label text-sm font-medium transition-colors ${
                        status === "submitting"
                          ? "cursor-wait bg-background-300 text-foreground-600"
                          : "bg-primary-500 text-background-50 hover:bg-primary-600"
                      }`}
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-base"></i>
                          正在提交
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line text-base"></i>
                          提交反馈
                        </>
                      )}
                    </button>
                    <p className="text-[11px] leading-relaxed text-foreground-500">
                      提交即表示你同意我们仅将信息用于内容改进。此表单为站内唯一反馈入口，不会用于其他用途。
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      {toast && <Toast message={toast} onClose={closeToast} />}
    </div>
  );
}